<template>
  <generic-in-app-screen>
    <b-modal
      ref="delete_modal"
      :title="$t('project_deletion')"
      @ok="do_delete_project"
      >{{ $t('confirm_deletion') }}
      <div slot="modal-ok">{{ $t('delete') }}</div>
      <div slot="modal-cancel">{{ $t('cancel') }}</div>
    </b-modal>

    <b-container>
      <h1 class="my-5">
        <b>{{ cur_project.name }} {{ $t('project_administration') }}</b>
      </h1>

      <h2 class="my-5">
        <b>{{ $t('project_status') }}</b>
      </h2>

      <b-form-checkbox v-model="local_is_in_production" switch size="lg">
        <div class="h5">
          <b>{{ $t('in_production') }}</b>
        </div>
      </b-form-checkbox>

      <b-form-checkbox
        v-model="local_autodelete_unreferenced_files"
        switch
        size="lg"
      >
        <div class="h5">
          <b>{{ $t('data_privacy_guard') }}</b>
        </div>
      </b-form-checkbox>

      <b-btn variant="danger" class="mt-3" @click="show_delete_modal">
        {{ $t('delete') }}
      </b-btn>

      <b-form-group :disabled="changes_disabled">
        <h2 class="my-5">
          <b>{{ $t('rename_project') }}</b>
        </h2>

        <b-row>
          <b-col cols="6">
            <renamer v-model="local_name" />
          </b-col>
        </b-row>

        <h2 class="my-5">
          <b>{{ $t('users_project_rights') }}</b>
        </h2>

        <b-table
          :fields="project_rights_table_fields"
          :items="project_rights_table_items"
          class="synaplus-cosmo-scroll-higher-720"
          striped
          hover
        >
          <template v-slot:cell(deletion)="row">
            <div v-if="row.item.user_id !== 0" class="ml-4">
              <b-btn-group>
                <b-btn
                  @click="toggle_project_right_edition(row.index)"
                  variant="outline-secondary"
                >
                  <span
                    class="fas fa-fw"
                    :class="
                      editing_project_rights_index === row.index
                        ? 'fa-check'
                        : 'fa-pen'
                    "
                  />
                </b-btn>
                <b-btn v-on="delete_button_on(row)" variant="outline-secondary">
                  <span
                    class="fas fa-fw"
                    :class="
                      editing_project_rights_index === row.index
                        ? 'fa-times'
                        : 'fa-trash'
                    "
                  />
                </b-btn>
              </b-btn-group>
            </div>
          </template>

          <template v-slot:cell(project_rights)="row">
            <b-form-select
              v-bind="project_rights_select_bindings(row)[0]"
              v-on="project_rights_select_bindings(row)[1]"
            />
          </template>

          <template v-slot:custom-foot>
            <b-tr>
              <b-td>
                <b-form-select
                  v-model="new_user_rights.user_id"
                  :options="unused_users_ids"
                  @input="changed_new_user_id"
                />
              </b-td>
              <b-td>
                <b-form-select
                  v-model="new_user_rights.username"
                  :options="unused_users_usernames"
                  @input="changed_new_user_name"
                />
              </b-td>
              <b-td>
                <b-form-select
                  v-model="new_user_rights.project_rights"
                  :options="project_group_choice_options"
                />
              </b-td>
              <b-td>
                <b-btn
                  :disabled="add_user_disabled"
                  @click="add_new_user_rights"
                  class="ml-4"
                  variant="outline-secondary"
                >
                  <span class="fas fa-plus fa-fw" />
                </b-btn>
              </b-td>
            </b-tr>
          </template>
        </b-table>

        <unroll-element
          :available="true"
          :title="$t('advanced_options_choice')"
          class="mt-3"
        >
          <h2 class="my-5">
            <b>{{ $t('code_to_execute') }}</b>
          </h2>
          <!-- N.B PL : What about changes_disabled here and in places concerning a project
      but that are neither a brick nor in brick_edition -->
          <togglable-code-editor v-model="local_project_code" />

          <h2 class="my-5">
            <b>{{ $t('metavariables_dicts') }}</b>
          </h2>
          <!--         <project-admin-subcomponent-metavariables-dicts-editor
          v-model="local_metavariables_dicts"
          :metavariables_types="cur_project.metavariables_list"
        /> -->
          <!--         <project-admin-subcomponent-metavariables-dicts-editor
          v-model="local_metavariables_dicts"
          :metavariables_types="cur_project.metavariables_types"
        /> -->
          <togglable-code-editor
            v-model="local_metavariables_dicts_definition_code"
          />

          <h5>{{ $t('output') }} :</h5>
          <div>
            {{ cur_project.metavariables_dicts_definition_code_stdoutput }}
          </div>

          <h5 class="text-danger">{{ $t('error') }} :</h5>

          <div class="text-danger">
            {{ cur_project.metavariables_dicts_definition_code_error }}
          </div>

          <h2 class="my-5">
            <b>{{ $t('metavariables_types') }}</b>
          </h2>

          <!--         <project-admin-subcomponent-metavariables-definition
          v-model="local_metavariables_list"
        /> -->
          <project-admin-subcomponent-metavariables-types-editor
            v-model="local_metavariables_types"
          />
        </unroll-element>
      </b-form-group>

      <validation-buttons-bar
        ref="validation_buttons_bar"
        :saved="!has_unsaved_changes"
        :is_validating="is_validating"
        @cancel="cancel"
        @save="save"
        @validate="validate"
      />
    </b-container>
  </generic-in-app-screen>
</template>

<i18n>
{
  "en": {
    "rename_project": "Rename project",
    "new_project_name": "New project name",
    "enter_name": "Enter the name",
    "code_to_execute": "Shared code for the project",
    "users_project_rights": "Project rights per user",
    "metavariables_dicts": "Metavariables dictionaries",
    "metavariables_types": "Metavariables types",
    "admin": "Admin",
    "writer": "Write",
    "reader": "Read",
    "user_id": "User Id",
    "username": "Username",
    "project_deletion": "Project deletion",
    "confirm_deletion": "Are you sure you want to permanently delete this project ?",
    "data_privacy_guard": "Data privacy guard"
  },
  "fr": {
    "rename_project": "Renommer le projet",
    "new_project_name": "Nouveau nom de projet",
    "enter_name": "Entrez le nom",
    "code_to_execute": "Code partagé pour le projet",
    "metavariables_dicts": "Dictionnaires de métavariables",
    "metavariables_types": "Types de métavariables",
    "users_project_rights": "Droits sur le projet par utilisateur",
    "admin": "Administrateur",
    "writer": "Exploitant",
    "reader": "Etude",
    "user_id": "Id Utilisateur",
    "username": "Login",
    "project_deletion": "Suppression du projet",
    "confirm_deletion": "Êtes-vous sûr de vouloir définitivement supprimer ce projet ?",
    "data_privacy_guard": "Protection pour la privauté des données"
  }
}
</i18n>

<script>
import _ from 'lodash'
import Vue from 'vue'

import { mapGetters } from 'vuex'

import {
  project_mixin,
  subscription_mixin,
  client_encryption_mixin,
  brick_mixin,
  validation_buttons_bar_wrapper_mixin,
} from '@/components/mixin.js'
import {
  create_project_rights,
  update_project_rights,
  delete_project_rights,
  get_rights_id,
  generic_update,
  delete_project,
} from '@/api/subscriptions.js'

import { project_auth_groups, dict_from_frozendict } from '@/utils.js'

import { FrozenDict } from './object_model.js'

import Renamer from '@/components/renamer.vue'
import TogglableCodeEditor from '@/components/togglable_code_editor.vue'
import UnrollElement from '@/components/unroll_element.vue'
import GenericInAppScreen from '@/components/generic_in_app_screen.vue'
import ProjectAdminSubcomponentMetavariablesDictsEditor from '@/components/project_admin_subcomponent_metavariables_dicts_editor.vue'
// import ProjectAdminSubcomponentMetavariablesDefinition from '@/components/project_admin_subcomponent_metavariables_definition.vue'
import ProjectAdminSubcomponentMetavariablesTypesEditor from '@/components/project_admin_subcomponent_metavariables_types_editor.vue'
import ValidationButtonsBar from '@/components/validation_buttons_bar.vue'

// *****************************************************************************

export default {
  name: 'ProjectAdmin',
  components: {
    Renamer,
    TogglableCodeEditor,
    UnrollElement,
    GenericInAppScreen,
    ValidationButtonsBar,
    ProjectAdminSubcomponentMetavariablesDictsEditor,
    // ProjectAdminSubcomponentMetavariablesDefinition,
    ProjectAdminSubcomponentMetavariablesTypesEditor,
  },
  mixins: [
    project_mixin,
    subscription_mixin,
    client_encryption_mixin,
    brick_mixin,
    validation_buttons_bar_wrapper_mixin,
  ],
  data() {
    return {
      local_name: '',
      local_is_in_production: false,
      local_project_code: '',

      editing_project_rights_index: -1,
      editing_project_rights_value: -1,

      local_project_rights: [],
      local_new_project_rights: [],

      new_user_rights: { user_id: -1, username: '', project_rights: -1 },

      // local_metavariables_dicts: [],
      local_metavariables_dicts_definition_code: '',

      // local_metavariables_list: [],
      local_metavariables_types: [],
      local_autodelete_unreferenced_files: false,
    }
  },
  computed: {
    _() {
      return _
    },
    cancel_path() {
      let project_id = this.project_id
      return `/projects/${project_id}/workflow`
    },

    // adapted_data() {
    //   let metavariables_dicts = _.map(this.local_metavariables_dicts, dict => {
    //     let frozen = new FrozenDict()
    //     _.forEach(Object.entries(dict), ([key, value]) => {
    //       frozen.setValue(key, value)
    //     })
    //     return frozen
    //   })

    //   let adapted = {
    //     uid: this.project_id,
    //     name: this.local_name,
    //     is_in_production: this.local_is_in_production,
    //     project_code: this.local_project_code,
    //     // metavariables_list: this.local_metavariables_types,
    //     metavariables_types: this.local_metavariables_types,
    //     metavariables_dicts: metavariables_dicts,
    //   }
    //   return adapted
    // },

    adapted_data() {
      // let metavariables_dicts = _.map(this.local_metavariables_dicts, dict => {
      //   let frozen = new FrozenDict()
      //   _.forEach(Object.entries(dict), ([key, value]) => {
      //     frozen.setValue(key, value)
      //   })
      //   return frozen
      // })

      let adapted = {
        uid: this.project_id,
        name: this.local_name,
        is_in_production: this.local_is_in_production,
        project_code: this.local_project_code,
        // metavariables_list: this.local_metavariables_types,
        metavariables_types: this.local_metavariables_types,
        // metavariables_dicts: this.local_metavariables_dicts,
        metavariables_dicts_definition_code: this
          .local_metavariables_dicts_definition_code,
        autodelete_unreferenced_files: this.local_autodelete_unreferenced_files,
      }
      return adapted
    },
    has_unsaved_changes() {
      return (
        !_.isMatchWith(this.cur_project, this.adapted_data, _.isEqual) ||
        !_.every(this.current_project_rights, elt =>
          _.isEqual(
            _.find(this.local_project_rights, ['user_id', elt.user_id]),
            elt
          )
        ) ||
        !_.isEmpty(this.local_new_project_rights)
      )
    },
    current_project_rights() {
      let res = _.map(
        this.automl_project_rights_undeleted_object.lists || [],
        v => this.automl_project_rights_undeleted_object.project_rights[v]
      )

      res = _.filter(res, ['project_id', this.project_id])

      return res
    },
    users_project_rights() {
      let res = _.map(
        this.current_project_rights,
        v => this.automl_users_undeleted_object.users[v.user_id]
      )
      return res
    },

    unused_users() {
      let res = _.filter(
        this.automl_users_undeleted_object.users,
        elt =>
          _.indexOf(_.map(this.users_project_rights, v => v.uid), elt.uid) ===
          -1
      )

      return res
    },
    unused_users_ids() {
      return _.map(this.unused_users, elt => elt.uid)
    },
    unused_users_usernames() {
      return _.map(this.unused_users, elt =>
        this.client_decrypt_string(elt.username)
      )
    },
    project_group_names() {
      return _.map(project_auth_groups, group => this.$t(group))
    },
    project_group_choice_options() {
      let res = _.map(
        Array.from(this.project_group_names.entries()),
        ([project_group_id, group_name]) => ({
          value: project_group_id,
          text: group_name,
        })
      )
      return res
    },

    all_project_rights() {
      return _.concat(this.local_project_rights, this.local_new_project_rights)
    },

    project_rights_table_fields() {
      return [
        { key: 'user_id', label: this.$t('user_id') },
        { key: 'username', label: this.$t('username') },
        { key: 'project_rights', label: this.$t('project_rights') },
        { key: 'deletion', label: '', class: 'w-col-2' },
      ]
    },

    project_rights_table_items() {
      let self = this
      return _.map(this.all_project_rights, function(elt, index) {
        return {
          user_id: elt.user_id,
          username: self.client_decrypt_string(
            self.automl_users_undeleted_object.users[elt.user_id].username
          ),
          project_rights:
            index < self.local_project_rights.length
              ? elt.project_group_id
              : elt.project_rights,
        }
      })
    },

    add_user_disabled() {
      return (
        this.new_user_rights.user_id === -1 ||
        this.new_user_rights.username === '' ||
        this.new_user_rights.project_rights === -1
      )
    },

    ...mapGetters([
      'automl_projects_undeleted_object',
      'automl_users_undeleted_object',
      'automl_project_rights_undeleted_object',
    ]),
  },
  watch: {
    // cur_project: {
    //   immediate: true,
    //   handler(new_val) {
    //     this.local_name = new_val.name || ''
    //     this.local_is_in_production = new_val.is_in_production
    //     this.local_project_code = new_val.project_code || ''
    //     // this.local_metavariables_list = _.cloneDeep(new_val.metavariables_list)
    //     this.local_metavariables_types = _.cloneDeep(
    //       new_val.metavariables_types
    //     )
    //     let local_metavariables_dicts = _.cloneDeep(new_val.metavariables_dicts)
    //     this.local_metavariables_dicts = _.map(local_metavariables_dicts, elt =>
    //       dict_from_frozendict(elt)
    //     )
    //   },
    // },

    cur_project: {
      immediate: true,
      handler(new_val) {
        this.local_name = new_val.name || ''
        this.local_is_in_production = new_val.is_in_production
        this.local_project_code = new_val.project_code || ''
        // this.local_metavariables_list = _.cloneDeep(new_val.metavariables_list)
        this.local_metavariables_types = _.cloneDeep(
          new_val.metavariables_types
        )
        // this.local_metavariables_dicts = _.cloneDeep(
        //   new_val.metavariables_dicts
        // )
        this.local_metavariables_dicts_definition_code =
          new_val.metavariables_dicts_definition_code
        // let local_metavariables_dicts = _.cloneDeep(new_val.metavariables_dicts)
        // this.local_metavariables_dicts = _.map(local_metavariables_dicts, elt =>
        //   dict_from_frozendict(elt)
        // )
        this.local_autodelete_unreferenced_files =
          new_val.autodelete_unreferenced_files
      },
    },
    current_project_rights: {
      immediate: true,
      handler(new_val) {
        this.local_project_rights = _.cloneDeep(new_val)
      },
    },
  },
  beforeMount() {
    this.fetch_collections(['projects', 'users', 'project_rights'])
  },
  beforeRouteLeave(to, from, next) {
    this.$refs.validation_buttons_bar.check_unsaved_changes(next)
  },
  methods: {
    toggle_project_right_edition(index) {
      if (this.editing_project_rights_index === index) {
        if (index < this.local_project_rights.length) {
          Vue.set(
            this.local_project_rights[index],
            'project_group_id',
            this.editing_project_rights_value
          )
        } else {
          const nindex = index - this.local_project_rights.length
          Vue.set(
            this.local_new_project_rights[nindex],
            'project_rights',
            this.editing_project_rights_value
          )
        }
        this.editing_project_rights_value = -1
        this.editing_project_rights_index = -1
      } else {
        if (index < this.local_project_rights.length) {
          this.editing_project_rights_value = this.local_project_rights[
            index
          ].project_group_id
        } else {
          const nindex = index - this.local_project_rights.length
          this.editing_project_rights_value = this.local_new_project_rights[
            nindex
          ].project_rights
        }
        this.editing_project_rights_index = index
      }
    },

    project_rights_select_bindings(row) {
      let bind = { options: this.project_group_choice_options }
      let on = {}

      if (this.editing_project_rights_index === row.index) {
        bind['value'] = this.editing_project_rights_value
        on['input'] = event => (this.editing_project_rights_value = event)
      } else {
        bind['value'] = row.item.project_rights
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },

    delete_button_on(row) {
      let res = {}

      if (this.editing_project_rights_index === row.index) {
        res['click'] = () => (this.editing_project_rights_index = -1)
      } else {
        res['click'] = () => this.delete_local_project_right(row.index)
      }

      return res
    },

    changed_new_user_id(value) {
      let corresponding_user = _.find(
        this.automl_users_undeleted_object.users,
        ['uid', value]
      )
      if (corresponding_user) {
        this.new_user_rights.username = this.client_decrypt_string(
          corresponding_user.username
        )
      }
    },
    changed_new_user_name(value) {
      let corresponding_user = _.find(
        this.automl_users_undeleted_object.users,
        x => this.client_decrypt_string(x.username) === value
      )
      if (corresponding_user) {
        this.new_user_rights.user_id = corresponding_user.uid
      }
    },
    add_new_user_rights() {
      this.local_new_project_rights.push(this.new_user_rights)
      this.new_user_rights = { user_id: -1, username: '', project_rights: -1 }
    },

    delete_local_project_right(index) {
      this.editing_project_rights_index = -1
      if (index < this.local_project_rights.length) {
        this.local_project_rights.splice(index, 1)
      } else {
        index = index - this.local_project_rights.length
        this.local_new_project_rights.splice(index, 1)
      }
    },
    async do_create_project_rights(new_project_rights) {
      await create_project_rights(this.project_id, {
        project_id: this.project_id,
        user_id: new_project_rights.user_id,
        project_group_id: new_project_rights.project_rights,
      })
    },

    async do_update_project_rights(project_rights) {
      await update_project_rights(this.project_id, {
        rights_id: project_rights.uid,
        project_group_id: project_rights.project_group_id,
      })
    },

    async do_delete_project_rights(rights_id) {
      let rights = {
        rights_id,
      }

      await delete_project_rights(this.project_id, rights)
    },
    async save_function() {
      let data = this.adapted_data
      let self = this
      await generic_update(this.project_id, data)

      let project_rights_to_delete_promises = _.map(
        this.current_project_rights,
        elt => elt.uid
      )
        .filter(
          uid => _.find(this.local_project_rights, ['uid', uid]) === undefined
        )
        .map(self.do_delete_project_rights)

      let project_rights_to_update_promises = _.map(
        this.local_project_rights,
        self.do_update_project_rights
      )

      let project_rights_to_create_promises = _.map(
        this.local_new_project_rights,
        self.do_create_project_rights
      )

      let all_promises = _.concat(
        project_rights_to_delete_promises,
        project_rights_to_update_promises,
        project_rights_to_create_promises
      )

      await Promise.all(all_promises)

      this.local_new_project_rights = []
    },

    show_delete_modal() {
      this.$refs.delete_modal.show()
    },

    async do_delete_project(evt) {
      evt.preventDefault()
      this.$refs.delete_modal.hide()
      await delete_project(this.project_id)
      this.$router.push('/')
    },
  },
}
</script>
